"use client";

import { Plus, Upload } from "lucide-react";
import { useEffect, useEffectEvent, useId, useRef, useState } from "react";
import { acceptAttribute, checkFiles, kindsLabel, type FileKind } from "@/lib/files";
import { cn } from "@/lib/utils";
type Props = {
  accepts: FileKind[];
  /** Most files the tool takes in total (1 = single file). */
  maxFiles?: number;
  /** Files already added, so "add more" respects maxFiles. */
  existing?: number;
  onFiles: (files: File[]) => void;
  /** `large`: the big hero box. `compact`: an "Add more files" button. */
  variant?: "large" | "compact";
  title?: string;
  subtitle?: string;
  /** Also take files pasted anywhere on the page (⌘V / Ctrl+V). */
  listenForPaste?: boolean;
};

/** Drag-and-drop, click to browse, or paste (FR-1). Wrong types, oversized
 *  files and too many files get a plain-language message (FR-2). */
export function DropZone({
  accepts,
  maxFiles = 1,
  existing = 0,
  onFiles,
  variant = "large",
  title = maxFiles > 1 ? "Drop files here" : "Drop a file here",
  subtitle,
  listenForPaste = variant === "large"
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [dragging, setDragging] = useState(false);
  const [messages, setMessages] = useState<{
    problems: string[];
    warnings: string[];
  }>({
    problems: [],
    warnings: []
  });
  function take(list: FileList | File[] | null | undefined) {
    const files = Array.from(list ?? []);
    if (files.length === 0) return;
    const isPhone = typeof window !== "undefined" && !!window.matchMedia?.("(pointer: coarse)").matches;
    const {
      accepted,
      problems,
      warnings
    } = checkFiles(files, {
      accepts,
      maxFiles,
      existing,
      isPhone
    });
    setMessages({
      problems,
      warnings
    });
    if (accepted.length > 0) onFiles(accepted);
  }
  const onPaste = useEffectEvent((e: ClipboardEvent) => {
    const files_0 = e.clipboardData?.files;
    if (!files_0 || files_0.length === 0) return;
    e.preventDefault();
    take(files_0);
  });
  useEffect(() => {
    if (!listenForPaste) return;
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [listenForPaste]);
  function openPicker() {
    inputRef.current?.click();
  }
  const dropHandlers = {
    onDragOver(e_0: React.DragEvent) {
      e_0.preventDefault();
      setDragging(true);
    },
    onDragLeave(e_1: React.DragEvent) {
      if (!e_1.currentTarget.contains(e_1.relatedTarget as Node | null)) {
        setDragging(false);
      }
    },
    onDrop(e_2: React.DragEvent) {
      e_2.preventDefault();
      setDragging(false);
      take(e_2.dataTransfer.files);
    }
  };
  const hint = `${kindsLabel(accepts)} · up to 100 MB${listenForPaste ? " · or paste with Ctrl+V / ⌘V" : ""}`;
  const input = <input ref={inputRef} type="file" className="sr-only" tabIndex={-1} aria-hidden="true" accept={acceptAttribute(accepts)} multiple={maxFiles > 1} data-testid="file-input" onChange={e_3 => {
    take(e_3.target.files);
    e_3.target.value = ""; // allow picking the same file again
  }} />;
  const feedback = <div aria-live="polite">
      {messages.problems.length + messages.warnings.length > 0 && <ul className="flex flex-col gap-1 text-left text-sm">
          {messages.problems.map(m => <li key={m} className="font-medium text-destructive">
              {m}
            </li>)}
          {messages.warnings.map(m_0 => <li key={m_0} className="text-ink">
              {m_0}
            </li>)}
        </ul>}
    </div>;
  if (variant === "compact") {
    return <div className="flex flex-col gap-2">
        <button type="button" onClick={openPicker} {...dropHandlers} className={cn("flex min-h-11 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-line-strong px-4 font-semibold text-ink hover:bg-sand", dragging && "border-brand bg-brand-tint")}>
          <Plus className="size-5" aria-hidden="true" />
          Add more files
        </button>
        {input}
        {feedback}
      </div>;
  }
  return <div className="flex flex-col gap-3">
      <div {...dropHandlers} onClick={openPicker} className={cn("flex cursor-pointer flex-col items-center gap-4 rounded-[14px] border-2 border-dashed border-line-strong bg-[#fafbff] px-5 py-10 text-center transition-colors sm:px-6 sm:py-12", dragging && "border-brand bg-brand-tint")}>
        <Upload className="size-14 text-brand" strokeWidth={1.6} aria-hidden="true" />
        <p className="font-heading text-[22px] leading-tight font-semibold sm:text-[26px]">
          {title}
        </p>
        {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        <button type="button" aria-describedby={hintId} onClick={e_4 => {
        e_4.stopPropagation();
        openPicker();
      }} className="min-h-12 rounded-lg bg-primary px-7 font-semibold text-primary-foreground hover:bg-primary/90">
          Choose files
        </button>
        <p id={hintId} className="text-sm text-muted-foreground">
          {hint}
        </p>
      </div>
      {/* Outside the clickable box so input.click() doesn't bubble back into it */}
      {input}
      {feedback}
    </div>;
}